'use client';

import { useEffect, useState } from 'react';
import { PopularConversionsCard } from '@/components/PopularConversionsCard';
import { categories } from '@/lib/data/categories';
import { getUsageData } from '@/lib/dataService';
import { converters } from '@/lib/converters';
import * as Icons from 'lucide-react';
import Link from 'next/link';
import { LucideIcon } from 'lucide-react';
import { Converter } from '@/lib/types/converter';

type ConverterClientProps = {
  converter: Converter;
  converterId: string;
};

export function ConverterClient({ converter, converterId }: ConverterClientProps) {
  const [popularConversions, setPopularConversions] = useState<
    { from: string; to: string; count: number }[]
  >([]);

  useEffect(() => {
    const usageData = getUsageData();
    console.log('Usage Data:', usageData);
    
    const conversions = Object.keys(usageData).length > 0 
      ? Object.entries(usageData)
          .map(([key, count]) => {
            const [from, to] = key.split('-');
            return { from, to, count };
          })
          .sort((a, b) => b.count - a.count)
          .slice(0, 15)
      : [
          { from: 'meter', to: 'foot', count: 100 },
          { from: 'celsius', to: 'fahrenheit', count: 90 },
          { from: 'kilogram', to: 'pound', count: 80 }
        ];
    
    setPopularConversions(conversions);
  }, []);

  // Find category and handle undefined case
  const category = categories.find((c) => c.converters.includes(converter.id));
  if (!category) {
    console.error(`Category not found for converter: ${converterId}`);
    return (
      <div className="grid gap-4">
        <PopularConversionsCard popularConversions={popularConversions} />
        <div className="text-red-500">
          Error: Unable to find category for this converter
        </div>
      </div>
    );
  }

  console.log('Category:', category);
  console.log('Converter ID:', converterId);

  return (
    <div className="grid gap-4">
      <PopularConversionsCard popularConversions={popularConversions} />

      {category && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Icons.List className="w-5 h-5 text-emerald-600" />
            Other converters in this category
          </h3>
          <div className="flex flex-wrap gap-2">
            {category.converters
              .filter((id) => id !== converter.id)
              .map((id) => {
                const relatedConverter = converters[id];
                if (!relatedConverter) return null;

                const IconComponent = Icons[relatedConverter.icon as keyof typeof Icons] as LucideIcon | undefined;
                return (
                  <Link 
                    href={`/${category.name.toLowerCase().replace(/\s+/g, '-')}/${relatedConverter.name.toLowerCase().replace(/\s+/g, '-')}`} 
                    key={id} 
                    className="hover-lift"
                  >
                    <div className="px-3 py-1.5 border rounded-full bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-600 flex items-center gap-2 transition-all">
                      {IconComponent ? <IconComponent className="w-4 h-4 text-emerald-600" /> : null}
                      <span className="text-sm font-medium">{relatedConverter.name}</span>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Icons.List className="w-5 h-5 text-emerald-600" />
          Other Categories
        </h3>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const IconComponent = Icons[category.icon as keyof typeof Icons] as LucideIcon | undefined;
            return (
              <Link 
                href={`/${category.name.toLowerCase().replace(/\s+/g, '-')}`} 
                key={category.id} 
                className="hover-lift"
              >
                <div className="px-3 py-1.5 border rounded-full bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-600 flex items-center gap-2 transition-all">
                  {IconComponent ? <IconComponent className="w-4 h-4 text-emerald-600" /> : null}
                  <span className="text-sm font-medium">{category.name}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
